import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useData } from "../lib/DataContext";
import { PageHeader, TierBadge, Stepper, Qty, Panel } from "../components/shared";

function Preview({ productId, qty, tick }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!productId || qty <= 0) { setData(null); return; }
    api.get("/production/preview", { params: { product_id: productId, quantity: qty } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [productId, qty, tick]);
  if (!data) return null;
  return (
    <Panel className="overflow-hidden" data-testid="bom-preview">
      <div className="px-5 py-3 border-b border-[#1E293B] font-mono text-xs tracking-[0.2em] text-[#9CA3AF]">BOM DEDUCTION PREVIEW</div>
      <div className="divide-y divide-[#1E293B]">
        {data.lines.map((l) => (
          <div key={l.item_id} data-testid={`preview-line-${l.sku}`} className={`grid grid-cols-12 items-center gap-2 px-5 py-3 text-sm ${l.short ? "bg-[#EF4444]/10" : ""}`}>
            <div className="col-span-12 sm:col-span-5 flex items-center gap-2"><TierBadge tier={l.tier} /><span className="truncate">{l.name}</span></div>
            <div className="col-span-4 sm:col-span-2 text-right"><Qty v={l.stock} unit={l.unit} /></div>
            <div className="col-span-4 sm:col-span-2 text-right text-[#F59E0B]">−<Qty v={l.required} unit="" /></div>
            <div className={`col-span-4 sm:col-span-3 text-right font-bold ${l.short ? "text-[#EF4444]" : "text-[#10B981]"}`}>→ <Qty v={l.after} unit={l.unit} /></div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export default function Produce() {
  const { items, boms, refreshAll } = useData();
  const bomIds = new Set(boms.map((b) => b.product_id));
  const [tier, setTier] = useState(3);
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const producible = items.filter((i) => i.tier === tier && bomIds.has(i.id) && (!ql || `${i.name} ${i.sku} ${i.category || ""} ${i.notes || ""} ${i.shopify_sku || ""}`.toLowerCase().includes(ql)));
  const [pid, setPid] = useState(null);
  const [qty, setQty] = useState(10);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const product = items.find((i) => i.id === pid);

  const register = async (force = false) => {
    setBusy(true);
    try {
      const r = await api.post("/production", { product_id: pid, quantity: qty, force });
      toast.success(`Registered ${qty} × ${product.name}. Now ${r.data.product.stock} on hand.`);
      await refreshAll(); setTick((t) => t + 1);
    } catch (e) {
      if (e?.response?.status === 409) {
        const s = e.response.data.detail.shortages.map((x) => x.name).join(", ");
        toast.error(`Insufficient: ${s}`, { action: { label: "Register anyway", onClick: () => register(true) }, duration: 10000 });
      } else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader kicker="// ONE-TAP PRODUCTION" title="Register Production">
        {[3, 2].map((t) => (
          <button key={t} data-testid={`produce-tier-${t}`} onClick={() => { setTier(t); setPid(null); }}
            className={`h-12 px-5 rounded-md text-sm font-semibold border ${tier === t ? "bg-white text-[#090D16]" : "border-[#334155] text-[#9CA3AF]"}`}>
            {t === 3 ? "Finished Goods" : "Preproduction"}
          </button>
        ))}
      </PageHeader>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="font-mono text-xs tracking-[0.2em] text-[#6B7280]">1 · SELECT PRODUCT</div>
        <input data-testid="produce-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, variant or SKU…"
          className="h-12 w-full sm:w-80 px-4 bg-[#0F1623] border border-[#334155] rounded-md focus:outline-none focus:border-[#F59E0B]" />
      </div>
      {!producible.length && <div data-testid="produce-search-empty" className="text-sm text-[#6B7280] mb-6">No {tier === 3 ? "finished goods" : "preproduction items"} match "{q}".</div>}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 mb-10">
        {producible.map((p) => (
          <button key={p.id} data-testid={`produce-product-${p.sku}`} onClick={() => setPid(p.id)}
            className={`text-left p-5 min-h-[96px] rounded-md border-2 transition-colors active:scale-[0.98] ${pid === p.id ? "border-[#F59E0B] bg-[#F59E0B]/10" : "border-[#1E293B] bg-[#111827] hover:border-[#334155]"}`}>
            <div className="font-semibold text-lg leading-tight">{p.name}</div>
            <div className="font-mono text-xs text-[#9CA3AF] mt-2">ON HAND <span className="text-white">{p.stock}</span></div>
          </button>
        ))}
        {!producible.length && <div className="text-[#6B7280] col-span-full">No products with a linked BOM in this tier.</div>}
      </div>
      {product && (
        <div className="grid xl:grid-cols-2 gap-8 rise">
          <div>
            <div className="font-mono text-xs tracking-[0.2em] text-[#6B7280] mb-3">2 · QUANTITY PRODUCED</div>
            <Panel className="p-6">
              <div className="font-display text-2xl font-bold mb-5" data-testid="produce-selected-name">{product.name}</div>
              <Stepper value={qty} onChange={setQty} min={0} testId="production-quantity-stepper" big />
              <div className="flex flex-wrap gap-2 mt-4">
                {[1, 5, 10, 25, 50].map((n) => (
                  <button key={n} data-testid={`qty-quick-add-${n}`} onClick={() => setQty(qty + n)}
                    className="h-12 px-4 rounded-md border border-[#334155] font-mono font-bold hover:border-[#F59E0B]">+{n}</button>
                ))}
                <button data-testid="qty-reset" onClick={() => setQty(0)} className="h-12 px-4 rounded-md border border-[#334155] font-mono text-[#9CA3AF]">CLR</button>
              </div>
              <button data-testid="register-production-btn" disabled={busy || qty <= 0} onClick={() => register(false)}
                className="mt-8 w-full h-20 rounded-md bg-[#F59E0B] text-[#090D16] font-display font-black text-2xl tracking-wide flex items-center justify-center gap-3 disabled:opacity-40 active:scale-[0.98] transition-transform hover:bg-[#FBBF24]">
                <CheckCircle2 className="w-8 h-8" />{busy ? "REGISTERING…" : "REGISTER PRODUCTION"}
              </button>
              <div className="flex items-center gap-2 text-xs text-[#6B7280] mt-3"><AlertTriangle className="w-3.5 h-3.5" />Materials are deducted automatically from the linked BOM.</div>
            </Panel>
          </div>
          <div>
            <div className="font-mono text-xs tracking-[0.2em] text-[#6B7280] mb-3">3 · MATERIAL IMPACT</div>
            <Preview productId={pid} qty={qty} tick={tick} />
          </div>
        </div>
      )}
    </div>
  );
}
